import React, { useRef, useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Card from './Card';
import Pagination from '../../core/Pagination';
import Filters from './Filters';
import T from 'i18n-react';
import { STARTDATE, ENDDATE, TAG, REGION } from './variables';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import AppContainer from '../Container/AppContainer';
import useIsMounted from 'ismounted';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../core/fetch';
import NearbyDescription from './NearbyDescription';
import NearbyDialog from './NearbyDialog';
import LogoImage from '../../../images/icons/logo.png';
import Link from '@material-ui/core/Link';
import AuthDialogContextProvider from '../components/AuthDialog/AuthDialogContextProvider';
import { PLANS } from '../routing';
import { UsersitePlansGetManyTypePayload } from '~/models/schema';
import AppDialog from '../../core/Dialog/AppDialog';

const useStyles = makeStyles((theme) => ({
  grid: {
    margin: theme.spacing(-1.5),
    width: `calc(100% + ${theme.spacing(1.5) * 2}px)`,
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(-1),
      width: `calc(100% + ${theme.spacing(1) * 2}px)`,
    },
    '& > *': {
      padding: theme.spacing(1.5),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1),
      },
    },
  },
  filter: {
    paddingBottom: theme.spacing(4),
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      textAlign: 'left',
    },
  },
  pagination: {
    marginTop: theme.spacing(8),
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(27),
    },
  },
  notFound: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  notFoundTitle: {
    fontWeight: 'bold',
    padding: theme.spacing(4, 0, 8),
    fontSize: theme.typography.pxToRem(28),
    color: '#FF6E2E',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(24),
    },
  },
  recruitment: {
    background: 'linear-gradient(transparent 70%, #ffdaca 60%)',
    display: 'inline',
    color: '#3a3a3a',
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(24),
  },
  recruitmentLink: {
    color: '#ff4f00',
    '&:hover': {
      textDecoration: 'none',
      color: '#f99466',
    },
  },
  logo: {},
}));

interface PlansProps extends UsersitePlansGetManyTypePayload {
  apiPath?: string;
  classes?: { [key: string]: string };
}

const TYPE_COMPONENTS = {
  nearby: { desc: NearbyDescription, dialog: NearbyDialog },
};

const SESSION_KEY = 'plans_dialog';

const Plans = ({
  plans: plansProps,
  acceptingPlans: acceptingPlansProps,
  tags: tagsProps,
  isAuthenticated,
  count: countProps,
  title,
  regions: regionsProps,
  apiPath,
  classes: classesProps,
}: PlansProps) => {
  const classes = useStyles({ classes: classesProps });

  const isMounted = useIsMounted();

  const { enqueueSnackbar } = useSnackbar();

  const dialogRef = useRef<typeof AppDialog>();

  const getHistoryState = () => window.history?.state || {};

  const searchParams = new URLSearchParams(window.location.search);

  const [state, setState] = useState(() => {
    const startDate = searchParams.get(STARTDATE) || '';

    const endDate = searchParams.get(ENDDATE) || '';

    const tag = parseInt(searchParams.get(TAG) || '0');

    const region = parseInt(searchParams.get(REGION) || '0');

    const historyPlans = getHistoryState().plans;

    const historyAcceptingPlans = getHistoryState().acceptingPlans;

    return {
      plans: Array.isArray(historyPlans) ? historyPlans : plansProps,
      acceptingPlans: Array.isArray(historyAcceptingPlans)
        ? historyAcceptingPlans
        : acceptingPlansProps,
      tags: tagsProps || [],
      regions: regionsProps || [],
      count: countProps,
      startDate,
      endDate,
      region: region > 0 ? region : null,
      tag: tag > 0 ? tag : null,
      featureName: title,
    };
  });

  const {
    plans,
    regions,
    tags,
    count,
    tag,
    region,
    featureName,
    startDate,
    endDate,
    acceptingPlans,
  } = state;

  const handleFavoriteChange = (key, defaultPlans) => (id, favorite, count) => {
    const historyPlans = getHistoryState()[key];

    const nextPlans = [
      ...(Array.isArray(historyPlans) ? historyPlans : defaultPlans || []),
    ];

    const plan = nextPlans.find(({ id: planId }) => planId === id);

    if (plan) {
      plan.isFavorite = favorite;
      plan.favoriteCount = count;
      // Store following state
      window.history?.replaceState?.(
        { ...(window.history.state || {}), [key]: nextPlans },
        '',
      );
    }
  };

  const updateState = (data = {}) =>
    setState((prevState) => ({ ...prevState, ...data }));

  const fetchPlans = (params?: URLSearchParams, onSuccess?: () => void) => {
    const query = params ? '?' + params.toString() : '';
    const apiQuery = query.startsWith('?')
      ? `${query}&includeSearchOptions=true`
      : '?includeSearchOptions=true';
    fetchJson(`${apiPath}${apiQuery}`, {
      onSuccess: ({ json }) => {
        if (isMounted.current) {
          onSuccess?.();
          updateState(json);
          window.history?.pushState?.(
            { ...(window.history.state || {}), plans: json },
            '',
            `${window.location.pathname}${query}`,
          );
        }
      },
      onError: ({ messages = [T.translate('request.error')] }) =>
        isMounted.current &&
        messages.forEach((m) => enqueueSnackbar(m, { variant: 'error' })),
    });
  };

  const handleChangeFilter = (data) => {
    const params = new URLSearchParams(data);
    params.delete('page');
    updateState({
      startDate: data[STARTDATE],
      endDate: data[ENDDATE],
      region: data[REGION],
      tag: data[TAG],
    });
    fetchPlans(params);
  };

  const handleChangePage = (event, page) => {
    const params = new URLSearchParams(window.location.search);
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }
    fetchPlans(params, () => window.scrollTo(0, 0));
  };

  const { desc: Desc, dialog: Dialog } =
    (!isAuthenticated && TYPE_COMPONENTS[searchParams.get('type') || '']) || {};

  useEffect(() => {
    if (Dialog && !sessionStorage.getItem(SESSION_KEY)) {
      setTimeout(() => {
        if (isMounted.current) {
          dialogRef.current?.handleOpen?.();
          sessionStorage.setItem(SESSION_KEY, 'true');
        }
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthDialogContextProvider isLoggedIn={isAuthenticated}>
      <AppContainer>
        {!!(title || featureName) && (
          <>
            <Typography
              color="textPrimary"
              gutterBottom
              variant="h3"
              className={classes.title}
            >
              {title || featureName}
            </Typography>
            {!!Desc && <Desc />}
          </>
        )}
        {((regions && regions.length > 0) || (tags && tags.length > 0)) && (
          <div className={classes.filter}>
            <Filters
              tags={tags}
              regions={regions}
              tag={tag}
              region={region}
              startDate={startDate}
              endDate={endDate}
              onChange={handleChangeFilter}
              onClear={() => {
                updateState({ startDate: '', endDate: '', tag: null });
                fetchPlans();
              }}
            />
          </div>
        )}
        {acceptingPlans?.length > 0 ? (
          <>
            <div className={classes.notFound} id="plan_not_found">
              <img
                src={LogoImage}
                className={classes.logo}
                alt={T.translate('otetsutabi') as string}
              />
              <p className={classes.notFoundTitle}>
                {T.translate('plan.notFoundTitle')}
              </p>
              <p className={classes.recruitment}>
                {T.translate('plan.recruitment')}
                <Link href={PLANS} className={classes.recruitmentLink}>
                  {T.translate('plan.recruitmentLink')}
                </Link>
              </p>
            </div>
            <Grid container className={classes.grid}>
              {acceptingPlans.map((plan) => (
                <Grid item xs={6} sm={4} md={3} key={`plan-card${plan.id}`}>
                  <Card
                    {...plan}
                    isAuthenticated={isAuthenticated}
                    onChange={handleFavoriteChange(
                      'acceptingPlans',
                      acceptingPlans,
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <>
            <Grid container className={classes.grid}>
              {plans.map((plan) => (
                <Grid item xs={6} sm={4} md={3} key={`plan-card${plan.id}`}>
                  <Card
                    {...plan}
                    isAuthenticated={isAuthenticated}
                    onChange={handleFavoriteChange('plans', plans)}
                  />
                </Grid>
              ))}
            </Grid>
            {(count > 1 || plans.length > 0) && (
              <div className={classes.pagination}>
                <Pagination count={count} onChange={handleChangePage} />
              </div>
            )}
          </>
        )}
        {!!Dialog && <Dialog ref={dialogRef} />}
      </AppContainer>
    </AuthDialogContextProvider>
  );
};

export default Plans;
